import React, { useEffect, useState } from "react";
import { User } from "../../../types";
import { useOutletContext } from "react-router";
import { getCookie } from "../../../utils";

interface Invitation {
    id: number;
    installer: {
        id: number;
        email: string;
        name: string;
    };
    project: {
        id: number;
        name: string;
    };
    sites: Array<{
        id: number;
        name: string;
    }>;
    external_authorisations: Array<{
        id: number;
        manufacturer: string;
        external_account: string;
        external_id: string;
        external_token_id: string;
    }>;
    created_at: string;
    expires_at: string;
    finished: boolean;
}


const SettingTab = ({ label, content, defaultChecked }: { label: string, content: React.ReactNode, defaultChecked?: boolean }) => {
    return <>
        {
            defaultChecked ? (
                <input type="radio" name="settings_tabs" className="tab" aria-label={label} defaultChecked />
            ) : (
                <input type="radio" name="settings_tabs" className="tab" aria-label={label} />
            )
        }
        <div className="tab-content h-full w-full">{content}</div>
    </>
}

const InputField = ({ label, value, disabled }: { label: string, value: string, disabled?: boolean }) => {
    return <div className="flex flex-col gap-2">
        <label className="label">{label}</label>
        <input type="text" className="input text-sm disabled:bg-base-100 disabled:text-base-content" value={value} disabled={disabled} />
    </div>
}

const GeneralSettingsContent = ({ user }: { user: User }) => {
    return <fieldset className="fieldset text-xs m-4">
        <InputField label="Name" value={user.first_name + " " + user.last_name} disabled={true} />
        <InputField label="Email" value={user.email} disabled={true} />
        <InputField label="Organisation" value={user.organisation?.name || "-"} disabled={true} />
    </fieldset>
}

const InvitationsSettingsContent = ({ invitations }: { invitations: Invitation[] }) => {
    const getInvitationStatus = (invitation: Invitation) => {
        if (invitation.finished) {
            return { label: 'Finished', className: 'badge-success' };
        }
        const now = new Date();
        const expiresAt = new Date(invitation.expires_at);
        if (expiresAt < now) {
            return { label: 'Expired', className: 'badge-error' };
        }
        return { label: 'Active', className: 'badge-info' };
    };

    return <fieldset className="m-4">
        {invitations.length
            ? <div className="space-y-4">
                {invitations.map((invitation) => {
                    const status = getInvitationStatus(invitation);
                    return (
                        <div key={invitation.id} className="card bg-base-200 shadow">
                            <div className="card-body p-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-semibold text-base">{invitation.project.name}</h3>
                                        <p className="text-sm text-gray-600">
                                            {invitation.sites.length} site{invitation.sites.length > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className={`badge badge-sm ${status.className}`}>{status.label}</div>
                                        <div className="badge badge-sm badge-outline">{new Date(invitation.created_at).toLocaleDateString('en-AU')}</div>
                                    </div>
                                </div>
                            
                            <div className="divider my-2"></div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-xs text-gray-500">Installer</div>
                                    <div className="font-medium">{invitation.installer.name || invitation.installer.email}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Email</div>
                                    <div>{invitation.installer.email}</div>
                                </div>
                            </div>
                            
                            {invitation.external_authorisations && invitation.external_authorisations.length > 0 && (
                                <>
                                    <div className="divider my-2">External Authorisations</div>
                                    <div className="space-y-2">
                                        {invitation.external_authorisations.map((auth) => (
                                            <div key={auth.id} className="bg-base-100 p-2 rounded text-xs">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <span className="text-gray-500">Manufacturer:</span> <span className="font-medium">{auth.manufacturer}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Account:</span> <span className="font-medium">{auth.external_account}</span>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <span className="text-gray-500">External ID:</span> <span className="font-mono text-xs">{auth.external_id}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            
                            <div className="collapse collapse-arrow bg-base-100 mt-2">
                                <input type="checkbox" />
                                <div className="collapse-title text-xs font-medium">Sites ({invitation.sites.length})</div>
                                <div className="collapse-content">
                                    <ul className="text-xs space-y-1">
                                        {invitation.sites.map((site) => (
                                            <li key={site.id}>• {site.name}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>
            : <div className="text-sm">
                <p>You haven't sent any installer invitations yet.</p>
            </div>
        }
    </fieldset>
}

const SettingsPage: React.FC = () => {
    const { user } = useOutletContext<{ user: User }>();
    const [invitations, setInvitations] = useState<Invitation[]>([]);

    useEffect(() => {
        if (user) {
            fetch('/api/users/invitations/')
                .then(response => response.json())
                .then(data => {
                    setInvitations(data);
                });
        }
    }, [user]);

    return (
        <div className="w-full flex justify-center m-4 p-8">
            <div className="w-1/2 bg-pacificblue-600/25 rounded-lg p-6 h-full flex flex-col items-start gap-4">
                <h1 className="text-2xl font-semibold text-pacificblue-900"> Account Settings </h1>
                <div className="tabs tabs-border w-full">
                    <SettingTab label="Profile" content={<GeneralSettingsContent user={user} />} />
                    <SettingTab label="Invitations" content={<InvitationsSettingsContent invitations={invitations} />} defaultChecked={true} />
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;